import { Wifi, WifiOff } from 'lucide-react';

/**
 * LivePriceIndicator — Round 84k Phase C scaffolding (Feb 15, 2026).
 *
 * Tiny status chip that surfaces the SSE connection state to the user.
 * Currently the most common state is `dormant` — the SSE is connected
 * but the backend WebSocket plan is off. Once the user upgrades to
 * the paid plan and sets `MASSIVE_WEBSOCKET_ENABLED=true`, the chip
 * flips to a pulsing green "LIVE" dot.
 *
 * Hides itself entirely on `idle` (no tickers subscribed yet).
 *
 * Round 97l (Feb 2026) — Mobile layout-shift fix. Reconnecting state
 * used to swap the label to "Reconnecting", widening the pill from
 * ~38px to ~78px every time the SSE handshake broke. On phones the
 * whole header reflowed. We now keep the SAME "Live" label and width
 * across `connected` and `error` states and only repaint the colours:
 *   • connected = full emerald (text, border, dot, icon)
 *   • error      = amber border + dot + icon, label TEXT goes muted
 *                  light-grey, dot slow-flashes (3s vs the default 2s
 *                  emerald pulse). Same width, same shape, no
 *                  layout shift.
 * The hover tooltip still says "Reconnecting — stream interrupted"
 * so the meaning is preserved for anyone who wants the detail.
 */
export default function LivePriceIndicator({ status }) {
  // Hide while we're still establishing the connection. The brief
  // "Connecting" pill that appeared on every page load was wider
  // than the steady-state chip and shoved neighbours around.
  if (!status || status === 'idle' || status === 'connecting') return null;
  const isLive = status === 'connected';
  const isDormant = status === 'dormant';
  const isError = status === 'error';

  // Polling / dormant has no flicker problem so it stays as-is — its
  // own steady state, own width, own colour.
  if (isDormant) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border cursor-help bg-zinc-50 dark:bg-zinc-900/50 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-zinc-800"
        data-testid="live-price-indicator"
        title="Real-time streaming is dormant; prices refresh via polling."
      >
        <WifiOff className="w-2.5 h-2.5" />
        <span className="relative flex items-center">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-zinc-400" />
        </span>
        Polling
      </span>
    );
  }

  // Connected vs Error share the SAME label ("Live") so the pill
  // width never changes — only the accent colours flip.
  const chipClass = isError
    ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700'
    : 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800';
  const iconClass = isError
    ? 'w-2.5 h-2.5 text-amber-600 dark:text-amber-400'
    : 'w-2.5 h-2.5 text-emerald-700 dark:text-emerald-300';
  // Slower 3s pulse for the amber state so it reads as "interrupted /
  // retrying" rather than a steady heartbeat. Native Tailwind has no
  // .animate-pulse-slow utility so we set the animationDuration
  // inline — same keyframes, just a different beat.
  const dotClass = isError
    ? 'bg-amber-500 animate-pulse'
    : 'bg-emerald-500 animate-pulse';
  // Label tone: full emerald when live, muted light-grey when amber.
  const labelClass = isError
    ? 'text-zinc-400 dark:text-zinc-500'
    : 'text-emerald-700 dark:text-emerald-300';

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border cursor-help ${chipClass} ${labelClass}`}
      data-testid="live-price-indicator"
      title={
        isLive
          ? 'Live: real-time prices (15 min. delayed) via Massive WebSocket.'
          : 'Reconnecting — stream interrupted, retrying automatically.'
      }
    >
      <Wifi className={iconClass} />
      <span className="relative flex items-center">
        <span
          className={`inline-block w-1.5 h-1.5 rounded-full ${dotClass}`}
          style={isError ? { animationDuration: '3s' } : undefined}
        />
      </span>
      Live
    </span>
  );
}
